import { Divider } from 'primereact/divider';
import { Skeleton } from 'primereact/skeleton';
import { classNames } from 'primereact/utils';
import { useTranslation } from 'react-i18next';
import { Navigate, useParams } from 'react-router-dom';

import DownloadCertificateButton from '../../components/download-certificate-button/DownloadCertificateButton';
import EnclosedSitesGrid from '../../components/enclosed-sites-grid/EnclosedSitesGrid';
import { useGetCertificateDetailsById } from '../../hooks/api/certificates';
import { Locale } from '../../i18n';
import { parseDateWithoutTime } from '../../shared/helpers/parse-date';
import {
  CertificatePermissionReviewStatus,
  CertificateStatus,
  RequestReviewStatus,
} from '../../shared/interfaces/ICertificate';
import CertificateStatusTag from '../certificates/components/certificate-status-tag/CertificateStatusTag';
import SupplierCertificateReviewButton from '../certificates/components/supplier-certificate-review-button/SupplierCertificateReviewButton';
import SupplierCertificateReviewStatusTag from '../certificates/components/supplier-certificate-review-status-tag/SupplierCertificateReviewStatusTag';
import GoBackToSupplierCertificatesButton from './components/go-back-to-supplier-certificates-button/GoBackToSupplierCertificatesButton';
import styles from './SupplierCertificateDetailsPage.module.css';

const SupplierCertificateDetailsPage = (): JSX.Element => {
  const { certificateId } = useParams() as { certificateId: string };
  const {
    i18n: { language },
    t,
  } = useTranslation();

  const {
    data: certificate,
    isLoading,
    isError,
  } = useGetCertificateDetailsById(
    { certificateId },
    {
      enabled: !!certificateId,
      retry: 0,
    },
  );

  if (isError || (!isLoading && !certificate)) {
    return <Navigate to='/404' replace={true} />;
  }

  const canReview =
    certificate?.consumerReviewStatus !== CertificatePermissionReviewStatus.ACCEPTED &&
    certificate?.consumerReviewStatus !== CertificatePermissionReviewStatus.REJECTED &&
    certificate?.consumerReviewStatus !== RequestReviewStatus.REQUESTED &&
    certificate?.status !== CertificateStatus.EXPIRED;

  return (
    <div className='flex flex-column gap-4 md:gap-6 mt-2 md:mt-4'>
      <div className='flex flex-column gap-2'>
        <GoBackToSupplierCertificatesButton />

        {isLoading ? (
          <Skeleton height='40px' width='30%' />
        ) : (
          <div className='flex flex-column md:flex-row w-full align-items-center justify-content-between gap-2 md:gap-5'>
            <h1 className='text-3xl pt-2 m-0 w-full'>{certificate?.type}</h1>
            {canReview && (
              <SupplierCertificateReviewButton
                certificateId={certificate?.id?.toString() || ''}
                className='w-full md:w-auto p-button-outlined'
              />
            )}
          </div>
        )}
      </div>

      {/* Supplier Section */}
      <div className='flex flex-column gap-4 w-12 md:w-8'>
        <h4 className='m-0'>{t('supplierCertificateDetailsPage.sections.supplier.title')}</h4>

        {isLoading ? (
          <div className='grid grid-nogutter gap-4 md:gap-0'>
            <div className='col-12 md:col-4 md:pr-4'>
              <Skeleton height='3rem' />
            </div>
            <div className='col-12 md:col-4 md:pr-4'>
              <Skeleton height='3rem' />
            </div>
            <div className='col-12 md:col-4'>
              <Skeleton height='3rem' />
            </div>
          </div>
        ) : (
          <div className='grid grid-nogutter gap-4 md:gap-0'>
            <div className='col-12 md:col-4 md:pr-4 flex flex-column gap-1 md:gap-2'>
              <span className='font-medium'>
                {t('supplierCertificateDetailsPage.sections.supplier.fields.companyName')}
              </span>
              <span className='font-light text-black-alpha-60'>
                {certificate?.companyName || 'Unknown'}
              </span>
              <span className='font-light text-black-alpha-60 text-xs'>
                {certificate?.companyBpn || 'Unknown'}
              </span>
            </div>

            <div className='col-12 md:col-4 md:pr-4 flex flex-column gap-1 md:gap-2'>
              <span className='font-medium'>
                {t('supplierCertificateDetailsPage.sections.supplier.fields.dateReceived')}
              </span>
              <span className='font-light text-black-alpha-60'>
                {parseDateWithoutTime(certificate?.received, language as Locale)}
              </span>
            </div>

            <div className='col-12 md:col-4 flex flex-column gap-1'>
              <span className='font-medium'>
                {t('supplierCertificateDetailsPage.sections.supplier.fields.review')}
              </span>
              <div className='w-fit'>
                <SupplierCertificateReviewStatusTag status={certificate?.consumerReviewStatus} />
              </div>
            </div>
          </div>
        )}
      </div>

      <Divider className='p-0 m-0' />

      {/* General Details Section */}
      <div className='flex flex-column gap-4 w-12 md:w-8'>
        <div className='flex flex-row gap-3 align-items-center'>
          <h4 className='m-0'>
            {t('supplierCertificateDetailsPage.sections.generalDetails.title')}
          </h4>
          <DownloadCertificateButton
            certificateId={certificate?.id}
            pdfPath={certificate?.s3Path}
          />
        </div>

        <div className='grid grid-nogutter gap-4 md:gap-0'>
          <div className='col-12 md:col md:pr-4 flex flex-column gap-1 md:gap-2'>
            <span className='font-medium'>
              {t('supplierCertificateDetailsPage.sections.generalDetails.certificateType')}
            </span>
            <span className='font-light text-black-alpha-60'>{certificate?.type}</span>
          </div>

          <div className='col-12 md:col md:pr-4 flex flex-column gap-1 md:gap-2'>
            <span className='font-medium'>
              {t('supplierCertificateDetailsPage.sections.generalDetails.version')}
            </span>
            <span className='font-light text-black-alpha-60'>
              {certificate?.version || t('global.notSpecified')}
            </span>
          </div>

          <div className='col-12 md:col md:pr-4 flex flex-column gap-1 md:gap-2'>
            <span className='font-medium'>
              {t('supplierCertificateDetailsPage.sections.generalDetails.registrationNumber')}
            </span>
            <span className='font-light text-black-alpha-60'>
              {certificate?.registrationNumber || t('global.notSpecified')}
            </span>
          </div>

          <div className='col-12 md:col flex flex-column gap-1 md:gap-2'>
            <span className='font-medium'>
              {t('supplierCertificateDetailsPage.sections.generalDetails.documentId')}
            </span>
            <span className='font-light text-black-alpha-60'>
              {certificate?.documentId || t('global.notSpecified')}
            </span>
          </div>
        </div>

        <div className='grid grid-nogutter gap-4 md:gap-0'>
          <div className='col-12 md:col md:pr-4 flex flex-column gap-1 md:gap-2'>
            <span className='font-medium'>
              {t('supplierCertificateDetailsPage.sections.generalDetails.validFrom')}
            </span>
            <span className='font-light text-black-alpha-60'>
              {parseDateWithoutTime(certificate?.validFrom, language as Locale)}
            </span>
          </div>

          <div className='col-12 md:col md:pr-4 flex flex-column gap-1 md:gap-2'>
            <span className='font-medium'>
              {t('supplierCertificateDetailsPage.sections.generalDetails.validUntil')}
            </span>
            {certificate?.validUntil ? (
              <span className='font-light text-black-alpha-60'>
                {parseDateWithoutTime(certificate?.validUntil, language as Locale)}
              </span>
            ) : (
              <span className='font-light text-black-alpha-60'>
                {t('supplierCertificateDetailsPage.sections.generalDetails.noExpiration')}
              </span>
            )}
          </div>

          <div className='col-12 md:col md:pr-4 flex flex-column gap-1'>
            <span className='font-medium'>
              {t('supplierCertificateDetailsPage.sections.generalDetails.status')}
            </span>
            <div className='w-fit'>
              <CertificateStatusTag status={certificate?.status} />
            </div>
          </div>

          <div className='col-12 md:col' />
        </div>
      </div>

      {/* Issuer & Validation Section */}
      <div className='flex flex-column gap-4 w-12 md:w-8'>
        <h5 className='m-0'>{t('supplierCertificateDetailsPage.sections.issuer.title')}</h5>

        <div className='grid grid-nogutter gap-4 md:gap-0'>
          <div className='col-12 md:col md:pr-4 flex flex-column gap-1 md:gap-2'>
            <span className='font-medium'>
              {t('supplierCertificateDetailsPage.sections.issuer.issuer')}
            </span>
            <span className='font-light text-black-alpha-60'>
              {certificate?.issuerName || 'Unknown'}
            </span>
            <span className='font-light text-black-alpha-60 text-xs'>
              {certificate?.issuerBpn || 'Unknown'}
            </span>
          </div>

          <div className='col-12 md:col flex flex-column gap-1 md:gap-2'>
            <span className='font-medium'>
              {t('supplierCertificateDetailsPage.sections.issuer.trustLevel')}
            </span>
            <div className='flex flex-row gap-2 align-items-center'>
              <i
                className={classNames(
                  'pi pi-circle-fill',
                  styles['trust-level-' + certificate?.trustLevel],
                )}
                style={{ fontSize: '0.5rem' }}
              />
              <span className='font-light text-black-alpha-60'>
                {t(
                  `supplierCertificateDetailsPage.sections.issuer.trustLevels.${certificate?.trustLevel}`,
                )}
              </span>
            </div>
          </div>
        </div>
      </div>

      <Divider className='p-0 m-0' />

      {/* Enclosed Sites Section */}
      <div className='flex flex-column gap-4 w-12 md:w-8 mb-4'>
        <h4 className='m-0'>{t('supplierCertificateDetailsPage.sections.enclosedSites.title')}</h4>
        <span className='font-light text-black-alpha-60 -mt-2'>
          {t('supplierCertificateDetailsPage.sections.enclosedSites.text')}
        </span>
        <EnclosedSitesGrid
          sites={certificate?.enclosedSites}
          emptyText={t('supplierCertificateDetailsPage.sections.enclosedSites.emptyText')}
        />
      </div>
    </div>
  );
};

export default SupplierCertificateDetailsPage;
